/**
 * SolidColorService - Gestión de color para estilo de fondo sólido
 *
 * @version 1.0
 * @created 2026-03-01
 * @module core/map-engine
 *
 * @description
 * Servicio simple para gestionar el color del fondo sólido del mapa.
 * Almacena la preferencia del usuario en localStorage.
 *
 * @remarks
 * **Características**:
 * - Color por defecto: #0E2A38 (WESTEER dark blue)
 * - Persistencia en localStorage
 * - Validación de color hexadecimal
 * - Notificación de cambios mediante eventos
 */

const STORAGE_KEY = 'wayline-map-solid-color';
const DEFAULT_COLOR = '#0E2A38'; // WESTEER dark blue (--bg-secondary)

/**
 * Servicio singleton para gestionar el color del fondo sólido
 */
export class SolidColorService {
  private static instance: SolidColorService;
  private currentColor: string;
  private listeners: Set<(color: string) => void> = new Set();

  /**
   * Constructor privado (patrón Singleton)
   */
  private constructor() {
    // Cargar color guardado o usar default
    const saved = localStorage.getItem(STORAGE_KEY);
    this.currentColor = this.isValidHexColor(saved) ? saved : DEFAULT_COLOR;
  }

  /**
   * Obtiene la instancia única del servicio
   */
  public static getInstance(): SolidColorService {
    if (!SolidColorService.instance) {
      SolidColorService.instance = new SolidColorService();
    }
    return SolidColorService.instance;
  }

  /**
   * Obtiene el color actual
   */
  public getColor(): string {
    return this.currentColor;
  }

  /**
   * Establece un nuevo color y lo persiste
   */
  public setColor(color: string): void {
    if (!this.isValidHexColor(color)) {
      console.warn(`[SolidColorService] Color inválido: ${color}. Usando default.`);
      color = DEFAULT_COLOR;
    }

    this.currentColor = color;
    localStorage.setItem(STORAGE_KEY, color);

    // Notificar a listeners
    this.listeners.forEach(listener => listener(color));
  }

  /**
   * Suscribirse a cambios de color
   */
  public subscribe(listener: (color: string) => void): () => void {
    this.listeners.add(listener);
    // Retornar función de desuscripción
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Restablecer al color por defecto
   */
  public reset(): void {
    this.setColor(DEFAULT_COLOR);
  }

  /**
   * Valida que un string sea un color hexadecimal válido
   */
  private isValidHexColor(color: string | null): color is string {
    if (!color) return false;
    return /^#([0-9A-F]{3}){1,2}$/i.test(color);
  }
}

/**
 * Instancia singleton exportada del servicio
 */
export const solidColorService = SolidColorService.getInstance();
